import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Typography,
  List,
  ListItem,
  Checkbox,
  Button,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ToastContainer } from "react-toastify";
import ApiCallServices from "../../../../services/ApiCallServices";
import { END_POINTS } from "../../../../services/endpoint/endPoint";

const AiSuggestions = () => {
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectionError, setSelectionError] = useState(false);

  const [selectedGeo, setSelectedGeo] = useState(null);
  const [selectedSP, setSelectedSP] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);

  const [geoOptions, setGeoOptions] = useState([]);
  const [spOptions, setSpOptions] = useState([]);
  const [dealOptions, setDealOptions] = useState([]);

  const [loadingGeo, setLoadingGeo] = useState(true);
  const [loadingSP, setLoadingSP] = useState(false);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [prefersPersonalUI, setPrefersPersonalUI] = useState(false);

  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const brandmoSubdomainName = process.env.REACT_APP_BRANDMOSUBDOMAIN;
  const brandmoId = process.env.REACT_APP_BRANDMOUSERID;

  // Get subdomain (adjust if hosted on brandmo)
  const hostname = window.location.hostname;
  const subdomain = hostname.includes(brandmoSubdomainName)
    ? brandmoSubdomainName
    : hostname.split(".")[0];

  console.log("==========AI SUGGESTION=============");
  console.log(subdomain);
  console.log(brandmoSubdomainName);
  console.log("===========AI SUGGESTION End============");
  const handleSuggestionToggle = (sugg) => {
    setSelectedSuggestions((prev) => {
      const updated = prev.includes(sugg)
        ? prev.filter((item) => item !== sugg)
        : [...prev, sugg];

      if (updated.length > 0) {
        setSelectionError(false);
      }

      return updated;
    });
  };

  const handleSubmit = () => {
    if (selectedSuggestions.length === 0) {
      setSelectionError(true);
      return;
    }

    setSelectionError(false);
    setSubmitting(true);

    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 2000);
  };

  useEffect(() => {
    setSubmitted(false);
  }, [selectedSuggestions]);
  useEffect(() => {
    setSelectionError(false);
    setSelectedSuggestions([]);
    setSubmitted(false);
    if (!selectedDeal) return;

    setLoadingSuggestions(true); // Start loading

    // Generate random delay between 2000ms and 5000ms
    const randomDelay = Math.floor(Math.random() * (4000 - 1000 + 1)) + 1000;

    const timeout = setTimeout(() => {
      ApiCallServices.getlisting(
        `${END_POINTS.randomSuggestions}?limit=5&dealId=${selectedDeal.value}`
      )
        .then((res) => {
          if (res.status === 200) {
            setSuggestions(res.data?.message?.map((item) => item));
          }
        })
        .catch((err) => {
          console.error(
            "Error fetching suggestions",
            err?.response?.data?.error
          );
        })
        .finally(() => {
          setLoadingSuggestions(false); // Stop loading
        });
    }, randomDelay); // Apply random delay

    return () => clearTimeout(timeout); // Cleanup timeout if component unmounts or selectedDeal changes
  }, [selectedDeal]);

  // Load countries on mount
  useEffect(() => {
    ApiCallServices.getlisting(END_POINTS.countryList)
      .then((res) => {
        if (res.status === 200) {
          const countries = res.data.data.map((country) => ({
            value: country.ccode,
            label: country.name,
          }));
          setGeoOptions(countries);
        }
      })
      .catch((error) => {
        console.error(
          error?.response?.data?.error || "Error loading countries"
        );
      })
      .finally(() => setLoadingGeo(false));
  }, []);

  // Load SP when Geo changes
  useEffect(() => {
    setSelectedSP(null);
    setSelectedDeal(null);
    setDealOptions([]);
    setLoadingSP(true);

    ApiCallServices.create(END_POINTS.supplyPartnerCountryList, {
      countryCodes: selectedGeo ? [selectedGeo?.value] : [],
    })
      .then((res) => {
        if (res.status === 200) {
          const partners = res.data.data.map((sp) => ({
            value: sp.partner_id,
            label: sp.partner_name,
          }));
          setSpOptions(partners);
        }
      })
      .catch((error) => {
        console.error(
          error?.response?.data?.error || "Error loading supply partners"
        );
      })
      .finally(() => setLoadingSP(false));
  }, [selectedGeo]);

  // Load deals when SP is selected
  useEffect(() => {
    setSelectedDeal(null);
    setDealOptions([]);
    setLoadingDeals(true);

    const payload = {};
    if (selectedGeo) payload.countryCodes = [selectedGeo.value];
    if (selectedSP) payload.supplyPartnerIds = [selectedSP.value];

    ApiCallServices.create(END_POINTS.dealsSupplyCountryList, payload)
      .then((res) => {
        if (res.status === 200) {
          const deals = res.data.data.map((deal) => ({
            value: deal.id,
            label: `${deal.deal_name}`,
          }));
          setDealOptions(deals);
        }
      })
      .catch((error) => {
        console.error(error?.response?.data?.error || "Error loading deals");
      })
      .finally(() => setLoadingDeals(false));
  }, [selectedGeo, selectedSP]);

  useEffect(() => {
    let uiPref;

    if (subdomain === brandmoSubdomainName) {
      uiPref = brandmoId;
    } else {
      uiPref = localStorage.getItem("prefers_personal_ui");
    }

    setPrefersPersonalUI(uiPref === brandmoId);
  }, []);

  const isDealSelected = !!selectedDeal;

  return (
    <>
      <ToastContainer />
      <div
        className="container-fluid mt-1"
        style={{
          position: "relative",
          minHeight: "calc(100vh - 128px)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            overflowY: "auto",
            maxHeight: "calc(100vh - 230px)",
            paddingRight: 1,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "#0049aa",
              fontWeight: "600",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {prefersPersonalUI ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                BEAM &nbsp;
                <img
                  src={
                    "https://bi-api.tecreo.io/uploads/user_logo/brand-mo-logo.png"
                  }
                  alt="Brandmo"
                  style={{ width: "auto", height: 28 }}
                />
              </Box>
            ) : (
              "TASHA"
            )}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "#28a9e2", fontWeight: "600", mb: 0 }}
          >
            {prefersPersonalUI
              ? "BrandMo's Engine for Agentic Modeling"
              : "TeCreo's Agentic System for Human Assistance"}
          </Typography>

          <br />

          <div className="filter-wraper d-flex justify-content-start align-items-center gap-3 flex-wrap mt-0">
            {/* Geo Autocomplete */}
            <Autocomplete
              options={geoOptions}
              value={selectedGeo}
              onChange={(event, newValue) => {
                setSelectedGeo(newValue);
                setSelectedSP(null);
                setSelectedDeal(null);
              }}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              loading={loadingGeo}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Geo"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingGeo ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ minWidth: 300 }}
            />

            {/* Supply Partner Autocomplete */}
            <Autocomplete
              options={spOptions}
              value={selectedSP}
              onChange={(event, newValue) => {
                setSelectedSP(newValue);
                setSelectedDeal(null);
              }}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              loading={loadingSP}
              // disabled={spOptions.length === 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Supply Partner"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSP ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ minWidth: 300 }}
            />

            {/* Deal Autocomplete */}
            <Autocomplete
              options={dealOptions}
              value={selectedDeal}
              onChange={(event, newValue) => setSelectedDeal(newValue)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              loading={loadingDeals}
              // disabled={dealOptions.length === 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Deal"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDeals ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ minWidth: 300 }}
            />
          </div>

          {/* Suggestions Display */}
          {isDealSelected && (
            <Box className="mt-4" sx={{ px: 2, py: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Suggestions for:{" "}
                <Box
                  component="span"
                  sx={{ color: "#1976d2", fontWeight: 500 }}
                >
                  {selectedDeal.label}
                </Box>
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {loadingSuggestions ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 1,
                  }}
                >
                  <CircularProgress
                    size={48} // Increase size (default is 40)
                    thickness={5.5} // Make the stroke bolder (default is 3.6)
                    sx={{ color: "#1976d2" }} // Optional: change color
                  />
                </Box>
              ) : (
                <List dense sx={{ pl: 1 }}>
                  {suggestions?.map((sugg, idx) => (
                    <ListItem key={idx} disableGutters sx={{ py: 0 }}>
                      {/* Checkbox at START */}
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedSuggestions.includes(sugg)}
                          onChange={() => handleSuggestionToggle(sugg)}
                        />
                      </ListItemIcon>

                      <ListItemText primary={sugg} />
                    </ListItem>
                  ))}
                </List>
              )}
              {submitted && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "green", fontWeight: 600 }}
                  >
                    ✅ Your optimization request has been submitted successfully.
                  </Typography>
                </Box>
              )}
              {selectionError && (
                <Typography
                  variant="body2"
                  sx={{ color: "red", mt: 1, fontWeight: 500 }}
                >
                  Please select at least one suggestion.
                </Typography>
              )}
              {suggestions.length > 0 && !loadingSuggestions && !submitted && (
                <Box
                  sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    onClick={handleSubmit}
                    startIcon={
                      submitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : null
                    }
                  >
                    Optimising The Deal
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            p: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              borderTop: "1px solid #ccc",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#666", fontSize: "0.95rem", textAlign: "justify" }}
            >
              <strong>Disclaimer:</strong>

              {" "}{prefersPersonalUI ? "BrandMo’s" : "TeCreo’s"} AI-driven optimization recommendations are informed by historical data, 
              predictive modeling, and real-time signals. Outcomes may vary depending on market dynamics, input quality, 
              and campaign objectives. 
              Users are advised to exercise independent judgment when applying recommendations.{" "}
              {prefersPersonalUI ? "BrandMo" : "TeCreo"} does not warrant specific performance results and disclaims liability for variations arising 
              from AI-led optimizations.

            </Typography>
          </Box>
        </Box>
      </div>
    </>
  );
};

export default AiSuggestions;
